import { apiClient } from '../client/apiClient';
import { fetchCsrfToken } from '../client/fetchCsrfToken';

export type TourType = 'IN_PERSON' | 'SELF_GUIDED' | 'LIVE_VIDEO';

export type TourProvider = {
  dateOnly?: boolean;
  providerName?: string;
  supportsCancel?: boolean;
};

export type InstantTourProviders = {
  inPersonProvider?: TourProvider | null;
  liveVideoProvider?: TourProvider | null;
  selfGuidedProvider?: TourProvider | null;
};

export type AvailableTourTimes = {
  availableTimes: Array<string>;
  dateOnly: boolean;
  zoneId: string;
};

export type ScheduledTour = {
  listingAlias: string;
  tourId: string;
  tourType: TourType;
  startTime: string;
  endTime?: string;
  schedulingStatus?: string;
  supportsCancel?: boolean;
  zoneId?: string;
};

type HpToursResponse<T> = {
  success?: boolean;
  data?: T;
  message?: string;
};

type ScheduledToursForUserResponse = HpToursResponse<{
  scheduledTours?: Array<ScheduledTour>;
}>;

export type LatestAvailableTimesParams = {
  listingAlias: string;
  feedModelId?: string;
  tourType: TourType;
};

export type RequestTourBody = {
  listingAlias: string;
  feedModelId?: string;
  tourType: TourType;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  requestedTime: string;
  tourId?: string;
  smsConsent?: boolean;
  rentalSubmitId: string;
};

export type RescheduleTourBody = {
  listingAlias: string;
  feedModelId?: string;
  tourType: TourType;
  email: string;
  phone?: string;
  firstName: string;
  lastName: string;
  requestedTime: string;
  tourId: string;
  smsConsent?: boolean;
};

export type TourMutationResponse = HpToursResponse<ScheduledTour>;

export async function latestAvailableTimes({
  listingAlias,
  feedModelId = '',
  tourType,
}: LatestAvailableTimesParams): Promise<AvailableTourTimes> {
  const response = await apiClient<HpToursResponse<AvailableTourTimes>>({
    path: '/v3/hpTours/latestAvailableTimes',
    queryParams: {
      listingAlias,
      feedModelId,
      tourType,
    },
  });

  return {
    availableTimes: response.data?.availableTimes ?? [],
    dateOnly: response.data?.dateOnly ?? false,
    zoneId: response.data?.zoneId ?? '',
  };
}

export async function requestTour(body: RequestTourBody): Promise<TourMutationResponse> {
  return apiClient<TourMutationResponse>({
    method: 'POST',
    path: '/v3/hpTours/request',
    body: {
      feedModelId: '',
      tourId: '',
      smsConsent: false,
      ...body,
    },
  });
}

export async function rescheduleTour(body: RescheduleTourBody): Promise<TourMutationResponse> {
  return apiClient<TourMutationResponse>({
    method: 'POST',
    path: '/v3/hpTours/reschedule',
    body: {
      feedModelId: '',
      phone: '',
      smsConsent: false,
      ...body,
    },
  });
}

export async function cancelTour(tourId: string): Promise<HpToursResponse<boolean>> {
  const csrfToken = await fetchCsrfToken();
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  return apiClient<HpToursResponse<boolean>>({
    method: 'POST',
    path: '/v3/hpTours/cancel',
    queryParams: { tourId },
    headers,
  });
}

export async function scheduledToursForUser(): Promise<Array<ScheduledTour>> {
  const response = await apiClient<ScheduledToursForUserResponse>({
    path: '/v3/hpTours/scheduledToursForUser',
  });

  if (!response.success) {
    return [];
  }

  return (response.data?.scheduledTours ?? []).filter(
    (tour) => tour.schedulingStatus === 'REQUESTED' || tour.schedulingStatus === 'CONFIRMED',
  );
}

export const hpTours = {
  latestAvailableTimes,
  requestTour,
  rescheduleTour,
  cancelTour,
  scheduledToursForUser,
};
